import { Injectable, inject } from '@angular/core';
import { Observable, combineLatest, map, of, forkJoin } from 'rxjs';
import { catchError, tap, switchMap } from 'rxjs/operators';
import { ReferralApiService } from './referral/referral-api.service';
import { HospitalService } from './hospital.service';
import { ProgramService } from './program.service';
import { ProgramDefinitionsService } from './program-definitions.service';
import { getPatientName } from '../shared/utils/patient.util';
import { ReferralFormService } from './referral-form.service';
import {
  Referral,
  ReferralFilter,
  ReferralStatus,
  ReferralForm,
  FormStatus,
  Program,
  ProgramDefinitionHelpers,
  getValidReferralTransitions,
  getValidFormTransitions,
  type ReferralAttentionReason,
} from '@ics-referral-hub/shared-types';
import { Logger } from '../shared/utils/logger.util';

/**
 * Extended PatientDisplay interface that includes source information
 * and handles both referrals and referral forms
 */
export interface PatientDisplay {
  referralId: string;
  patientName: string;
  hospitalName: string;
  programName: string;
  referredDate: string;
  status: ReferralStatus;
  assignedTo?: string;
  lastStatusChangedBy?: string;
  remainDays?: string;
  programType?: string; // Type of the program (TCU or Community)
  /**
   * Outstanding items the referral still needs, derived server-side.
   * Drives the at-a-glance attention indicator in the referral list.
   */
  attentionReasons?: ReferralAttentionReason[];
}

export interface UnifiedPatientDisplay extends PatientDisplay {
  source: 'referral' | 'referral-form'; // Indicates the source of the data
  formId?: string; // Form ID if source is 'referral-form'
  formStatus?: FormStatus; // Original form status if source is 'referral-form'
  isEditable: boolean; // Whether the item can be edited
  editRoute: string; // The route to edit/view this item
}

/**
 * Parameters for filtering unified referral data
 */
export interface UnifiedReferralFilter {
  // Standard referral filters
  referralId?: string;
  patientName?: string;
  hospitalId?: string;
  hospitalIds?: string[];
  hospitalName?: string;
  programId?: string;
  programType?: 'TCU' | 'Community';
  programName?: string;
  status?: ReferralStatus[];
  archivedFilter?: 'active' | 'archived' | 'all';
  /** Only referrals that still need attention (e.g. missing mandatory documents) */
  needsAttention?: boolean;
  assignedTo?: string;
  submittedBy?: string;
  lastStatusChangedBy?: string;
  startDate?: string;
  endDate?: string;

  // Form-specific filters
  includeFormDrafts?: boolean; // Whether to include draft referral forms
  includeFormInProgress?: boolean; // Whether to include in-progress referral forms

  // Pagination
  limit?: number;
  offset?: number;
  page?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

@Injectable({
  providedIn: 'root',
})
export class UnifiedReferralService {
  private referralApi = inject(ReferralApiService);
  private referralFormService = inject(ReferralFormService);
  private hospitalService = inject(HospitalService);
  private programService = inject(ProgramService);
  private programDefinitionsService = inject(ProgramDefinitionsService);

  // Caches for resolving IDs to names
  private hospitalNameCache = new Map<string, string>();
  private programCache = new Map<string, Program>();
  private lookupsLoaded = false;

  /**
   * Ensures hospital and program lookup caches are populated.
   * Returns an observable that completes when caches are ready.
   */
  private ensureLookups(): Observable<void> {
    if (this.lookupsLoaded) {
      return of(undefined);
    }
    return forkJoin([
      this.hospitalService.getAll().pipe(
        tap(hospitals => hospitals.forEach(h => this.hospitalNameCache.set(h.id, h.name))),
        catchError(() => of([])),
      ),
      this.programService.getAll().pipe(
        tap(programs => programs.forEach(p => this.programCache.set(p.id, p))),
        catchError(() => of([])),
      ),
    ]).pipe(
      tap(() => { this.lookupsLoaded = true; }),
      map(() => undefined),
    );
  }

  private resolveHospitalName(hospitalId?: string): string {
    if (!hospitalId) return 'Unknown Hospital';
    return this.hospitalNameCache.get(hospitalId) || 'Unknown Hospital';
  }

  private resolveProgramName(programId?: string): string {
    if (!programId) return 'Unknown Program';
    return this.programCache.get(programId)?.name || 'Unknown Program';
  }

  private resolveProgramType(programId?: string): string | undefined {
    if (!programId) return undefined;
    return this.programCache.get(programId)?.type;
  }

  private resolveProgramSubtype(
    programId?: string,
    programSubtype?: string,
  ): string | undefined {
    if (programSubtype) {
      return programSubtype;
    }

    if (!programId) {
      return undefined;
    }

    const program = this.programCache.get(programId);
    if (!program) {
      return undefined;
    }

    const availableSubtypes = ProgramDefinitionHelpers.getProgramSubtypesForTags(
      program.type,
      program.tags,
      program.programDefinitionId,
      this.programDefinitionsService.getDefinitions(),
    );

    return availableSubtypes.length === 1 ? availableSubtypes[0].id : undefined;
  }

  private filterFormsByHospitalIds(
    forms: ReferralForm[],
    hospitalIds?: string[],
  ): ReferralForm[] {
    if (!hospitalIds?.length) {
      return forms;
    }

    return forms.filter((form) => hospitalIds.includes(form.hospitalId));
  }

  /**
   * Get unified patient display data combining referrals and referral forms
   */
  getUnifiedPatientDisplayData(
    filter?: UnifiedReferralFilter,
  ): Observable<{
    referrals: UnifiedPatientDisplay[];
    total: number;
    needsAttentionTotal: number;
  }> {
    Logger.log('UnifiedReferralService: Getting data with filter:', filter);

    // Determine what data to fetch based on filter
    const canLoadReferralForms = this.referralFormService.canCreateForm();
    const shouldIncludeForms =
      canLoadReferralForms &&
      !filter?.lastStatusChangedBy &&
      // Attention only applies to submitted referrals — unsubmitted forms surface
      // missing documents inline on the form itself.
      !filter?.needsAttention &&
      (
        filter?.includeFormDrafts ||
        filter?.includeFormInProgress ||
        this.shouldIncludeFormsForStatus(filter?.status)
      );

    // Always fetch referrals. A failure propagates to the caller's query so the
    // list can show an error instead of an empty "No referrals found" (MYB-5197).
    const referrals$ = this.referralApi.getAll(filter as ReferralFilter);

    if (!shouldIncludeForms) {
      // Only return referrals - backend handles sorting
      return referrals$.pipe(
        map(({ referrals, total, needsAttentionTotal }) => ({
          referrals: this.convertReferralsToUnified(referrals),
          total,
          needsAttentionTotal,
        })),
      );
    }

    // Fetch both referrals and forms with sorting parameters
    const formStatuses = this.getFormStatusesFromFilter(filter);
    const selectedHospitalIds =
      filter?.hospitalIds?.length
        ? filter.hospitalIds
        : filter?.hospitalId
          ? [filter.hospitalId]
          : [];
    const forms$: Observable<ReferralForm[]> = this.referralFormService
      .filterReferralForms({
        status: formStatuses?.[0],
        hospitalId:
          filter?.hospitalId ||
          (selectedHospitalIds.length === 1 ? selectedHospitalIds[0] : undefined),
        hospitalIds: selectedHospitalIds.length > 1 ? selectedHospitalIds : undefined,
        programId: filter?.programId,
        sortBy: filter?.sortBy,
        sortOrder: filter?.sortOrder,
      })
      .pipe(
        catchError((error) => {
          Logger.error('Error fetching referral forms:', error);
          return of([]);
        }),
      );

    // Ensure lookups are loaded before converting forms
    return this.ensureLookups().pipe(
      switchMap(() => combineLatest([referrals$, forms$])),
      map(([referralResponse, forms]) => {
        const { referrals, total, needsAttentionTotal } = referralResponse;
        const unifiedReferrals = this.convertReferralsToUnified(referrals);
        const unifiedForms = this.convertFormsToUnified(
          this.filterFormsByHospitalIds(forms, selectedHospitalIds),
        );

        // Combine data - backend handles sorting, no client-side sorting needed
        const combined = [...unifiedReferrals, ...unifiedForms];

        return { referrals: combined, total, needsAttentionTotal };
      }),
    );
  }

  /**
   * Get filtered unified data with advanced filtering options
   */
  getFilteredUnifiedData(
    filter: UnifiedReferralFilter,
  ): Observable<{
    referrals: UnifiedPatientDisplay[];
    total: number;
    needsAttentionTotal: number;
  }> {
    return this.getUnifiedPatientDisplayData(filter).pipe(
      map(({ referrals, total, needsAttentionTotal }) => ({
        referrals: this.applyClientSideFilters(referrals, filter),
        total,
        needsAttentionTotal,
      })),
    );
  }

  /**
   * Convert regular referrals to unified format
   */
  private convertReferralsToUnified(
    referrals: Referral[],
  ): UnifiedPatientDisplay[] {
    return referrals.map((referral) => {
      const patientName = getPatientName(referral.patient);
      const referralObj = referral as any;
      const programName = referralObj.program?.name || 'Unknown Program';
      const formattedProgramName = ProgramDefinitionHelpers.formatProgramDisplayName(
        programName,
        referral.programSubtype,
        undefined,
        this.programDefinitionsService.getDefinitions(),
      );

      return {
        referralId: referral.referralId || referral.id || '',
        patientName,
        hospitalName: referralObj.hospital?.name || 'Unknown Hospital',
        programName: formattedProgramName,
        programType: referralObj.program?.type,
        referredDate: new Date(referral.createdAt).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }),
        status: referral.status as ReferralStatus,
        assignedTo: referral.assignedTo,
        lastStatusChangedBy: referral.lastStatusChangedBy,
        remainDays: this.calculateRemainDays(referral.expiresAt),
        attentionReasons: referral.attentionReasons ?? [],
        source: 'referral' as const,
        isEditable: this.isReferralEditable(referral.status as ReferralStatus),
        editRoute: `/referrals/${referral.id}/profile`,
      };
    });
  }

  /**
   * Convert referral forms to unified format
   */
  private convertFormsToUnified(
    forms: ReferralForm[],
  ): UnifiedPatientDisplay[] {
    return forms.map((form) => {
      const patientName = this.getFormPatientName(form);
      const status = this.mapFormStatusToReferralStatus(form.status);
      const programName = this.resolveProgramName((form as any).programId);
      const programSubtype = this.resolveProgramSubtype(
        (form as any).programId,
        (form as any).programSubtype,
      );

      return {
        referralId: form.id || '', // Use form ID as referral ID for display
        patientName,
        hospitalName: this.resolveHospitalName((form as any).hospitalId),
        programName: ProgramDefinitionHelpers.formatProgramDisplayName(
          programName,
          programSubtype,
          undefined,
          this.programDefinitionsService.getDefinitions(),
        ),
        programType: this.resolveProgramType((form as any).programId),
        referredDate: new Date(form.createdAt).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }),
        status,
        assignedTo: form.assignedTo,

        // Unified-specific fields
        source: 'referral-form' as const,
        formId: form.id,
        formStatus: form.status,
        isEditable: this.isFormEditable(form.status),
        editRoute: `/referrals/form/${form.id}`,
      };
    });
  }

  /**
   * Calculate remaining days until expiration from expiresAt date
   */
  private calculateRemainDays(expiresAt?: Date | string): string | undefined {
    if (!expiresAt) return undefined;
    const expDate = new Date(expiresAt);
    const now = new Date();
    const diffMs = expDate.getTime() - now.getTime();
    if (diffMs <= 0) return '0';
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return String(days);
  }

  /**
   * Extract patient name from referral form
   */
  private getFormPatientName(form: ReferralForm): string {
    const firstName = form.patientInformation?.firstName || '';
    const lastName = form.patientInformation?.lastName || '';
    return `${firstName} ${lastName}`.trim() || 'Unknown Patient';
  }

  /**
   * Map form status to referral status for unified display
   */
  private mapFormStatusToReferralStatus(
    formStatus: FormStatus,
  ): ReferralStatus {
    switch (formStatus) {
      case FormStatus.DRAFT:
        return ReferralStatus.DRAFT;
      case FormStatus.IN_PROGRESS:
        return ReferralStatus.DRAFT; // Treat in-progress forms as drafts
      case FormStatus.SUBMITTED:
        return ReferralStatus.SUBMITTED;
      case FormStatus.UNDER_REVIEW:
        return ReferralStatus.UNDER_REVIEW;
      case FormStatus.ACCEPTED:
        return ReferralStatus.ACCEPTED;
      case FormStatus.REJECTED:
        return ReferralStatus.REJECTED;
      default:
        return ReferralStatus.DRAFT;
    }
  }

  /**
   * Determine if forms should be included based on status filter
   */
  private shouldIncludeFormsForStatus(statuses?: ReferralStatus[]): boolean {
    if (!statuses || statuses.length === 0) {
      return true; // No filter = include all data (both referrals and forms)
    }

    // Include forms if the filter includes draft, submitted, pending, or approved statuses
    // since these map to form statuses
    return statuses.some(
      (status) =>
        status === ReferralStatus.DRAFT ||
        status === ReferralStatus.SUBMITTED ||
        status === ReferralStatus.UNDER_REVIEW ||
        status === ReferralStatus.ACCEPTED ||
        status === ReferralStatus.REJECTED,
    );
  }

  /**
   * Get form statuses that correspond to the referral status filter
   */
  private getFormStatusesFromFilter(
    filter?: UnifiedReferralFilter,
  ): FormStatus[] | undefined {
    if (!filter?.status || filter.status.length === 0) {
      return undefined; // Return undefined to use getInProgressForms by default
    }

    const formStatuses: FormStatus[] = [];

    filter.status.forEach((status) => {
      switch (status) {
        case ReferralStatus.DRAFT:
          formStatuses.push(FormStatus.DRAFT, FormStatus.IN_PROGRESS);
          break;
        case ReferralStatus.SUBMITTED:
          formStatuses.push(FormStatus.SUBMITTED);
          break;
        case ReferralStatus.UNDER_REVIEW:
          formStatuses.push(FormStatus.UNDER_REVIEW);
          break;
        case ReferralStatus.ACCEPTED:
          formStatuses.push(FormStatus.ACCEPTED);
          break;
        case ReferralStatus.REJECTED:
          formStatuses.push(FormStatus.REJECTED);
          break;
      }
    });

    return formStatuses.length > 0 ? formStatuses : undefined;
  }

  /**
   * Check if a referral is editable based on its status
   * A referral is editable if it has valid transitions (not in terminal state)
   */
  private isReferralEditable(status: ReferralStatus): boolean {
    const validTransitions = getValidReferralTransitions(status as FormStatus);
    return validTransitions.length > 0;
  }

  /**
   * Check if a referral form is editable based on its status
   * A form is editable if it has valid transitions (not in terminal state)
   */
  private isFormEditable(status: FormStatus): boolean {
    const validTransitions = getValidFormTransitions(status);
    return validTransitions.length > 0;
  }

  /**
   * Sort unified data by specified criteria
   * NOTE: This method is no longer used since backend handles sorting for paginated requests
   * Keeping for reference in case client-side sorting is needed in the future
   */
  private sortUnifiedData(
    data: UnifiedPatientDisplay[],
    sortBy?: string,
    sortOrder?: 'ASC' | 'DESC',
  ): UnifiedPatientDisplay[] {
    if (!sortBy) {
      // Default sort by referredDate (most recent first)
      return data.sort(
        (a, b) =>
          new Date(b.referredDate).getTime() -
          new Date(a.referredDate).getTime(),
      );
    }

    const multiplier = sortOrder === 'DESC' ? -1 : 1;

    return data.sort((a, b) => {
      const aValue = this.getSortValue(a, sortBy);
      const bValue = this.getSortValue(b, sortBy);

      if (aValue < bValue) return -1 * multiplier;
      if (aValue > bValue) return 1 * multiplier;
      return 0;
    });
  }

  /**
   * Get value for sorting
   * NOTE: This method is no longer used since backend handles sorting for paginated requests
   * Keeping for reference in case client-side sorting is needed in the future
   */
  private getSortValue(item: UnifiedPatientDisplay, sortBy: string): any {
    switch (sortBy) {
      case 'referredDate':
        return new Date(item.referredDate).getTime();
      case 'patientName':
        return item.patientName.toLowerCase();
      case 'status':
        return item.status;
      case 'hospitalName':
        return item.hospitalName.toLowerCase();
      case 'programName':
        return item.programName.toLowerCase();
      default:
        return (item as any)[sortBy] || '';
    }
  }

  /**
   * Apply client-side filters to unified data
   */
  private applyClientSideFilters(
    data: UnifiedPatientDisplay[],
    filter: UnifiedReferralFilter,
  ): UnifiedPatientDisplay[] {
    let filtered = [...data];

    // Apply search filters
    if (filter.referralId) {
      const term = filter.referralId.toLowerCase();
      filtered = filtered.filter((item) =>
        item.referralId.toLowerCase().includes(term),
      );
    }

    if (filter.patientName) {
      const term = filter.patientName.toLowerCase();
      filtered = filtered.filter((item) =>
        item.patientName.toLowerCase().includes(term),
      );
    }

    if (filter.hospitalName) {
      const term = filter.hospitalName.toLowerCase();
      filtered = filtered.filter((item) =>
        item.hospitalName.toLowerCase().includes(term),
      );
    }

    if (filter.programName) {
      const term = filter.programName.toLowerCase();
      filtered = filtered.filter((item) =>
        item.programName.toLowerCase().includes(term),
      );
    }

    // Apply pagination
    if (filter.offset || filter.limit) {
      const start = filter.offset || 0;
      const end = filter.limit ? start + filter.limit : undefined;
      filtered = filtered.slice(start, end);
    }

    return filtered;
  }
}
